/**
 * Room adapter LiveKit per `VideoCall.tsx` (Task #1428).
 *
 * Engine video: LiveKit self-hosted (cluster BE-3/BE-4, wss://media.easyflowai.it).
 * L'adapter espone una surface stabile e provider-agnostic per la page
 * `VideoCall.tsx`: `Video.connect`, `LocalVideoTrack/AudioTrack/DataTrack`,
 * `createLocalVideoTrack/AudioTrack`, eventi participant
 * `trackSubscribed`/`trackUnsubscribed`/`trackEnabled`/`trackDisabled`/
 * `networkQualityLevelChanged`, plus il flusso DataTrack-via-`message`.
 *
 * Il rewrite di `VideoCall.tsx` in stile LiveKit nativo (eliminazione
 * dell'adapter) è coperto dal follow-up #1433.
 */

import {
  Room as LKRoom,
  RoomEvent,
  ParticipantEvent,
  ConnectionQuality,
  Track,
  createLocalVideoTrack as lkCreateLocalVideoTrack,
  createLocalAudioTrack as lkCreateLocalAudioTrack,
  LocalVideoTrack as LKLocalVideoTrack,
  LocalAudioTrack as LKLocalAudioTrack,
  type RemoteParticipant as LKRemoteParticipant,
  type RemoteTrack as LKRemoteTrack,
  type RemoteTrackPublication as LKRemoteTrackPublication,
  type LocalParticipant as LKLocalParticipant,
  type LocalTrackPublication as LKLocalTrackPublication,
  type Participant as LKParticipant,
} from 'livekit-client';
import { EventEmitter } from 'eventemitter3';

/* ─── enable/disable shim → livekit mute/unmute ─────────────────────────── */

interface LocalTrackToggle {
  enable(): Promise<unknown>;
  disable(): Promise<unknown>;
}

const installToggleShim = (proto: object): void => {
  const p = proto as Record<string, unknown>;
  if (typeof p.enable !== 'function') {
    p.enable = function (this: { unmute: () => Promise<unknown> }): Promise<unknown> {
      return this.unmute();
    };
  }
  if (typeof p.disable !== 'function') {
    p.disable = function (this: { mute: () => Promise<unknown> }): Promise<unknown> {
      return this.mute();
    };
  }
};
installToggleShim(LKLocalVideoTrack.prototype);
installToggleShim(LKLocalAudioTrack.prototype);

/* ─── tipi surface adapter ─────────────────────────────────────────────── */

export type LocalVideoTrackInstance = LKLocalVideoTrack & LocalTrackToggle;
export type LocalAudioTrackInstance = LKLocalAudioTrack & LocalTrackToggle;
export type LocalVideoTrack = LocalVideoTrackInstance;
export type LocalAudioTrack = LocalAudioTrackInstance;

export const LocalVideoTrack = LKLocalVideoTrack as unknown as new (
  mediaStreamTrack: MediaStreamTrack,
  options?: { name?: string },
) => LocalVideoTrackInstance;
export const LocalAudioTrack = LKLocalAudioTrack as unknown as new (
  mediaStreamTrack: MediaStreamTrack,
  options?: { name?: string },
) => LocalAudioTrackInstance;

/**
 * Track remoto adapter: tipo strutturale con surface unificata (attach/detach
 * solo per audio/video — il consumer guard via `kind` prima di chiamarli).
 *
 * `attach(element?)` / `detach(element?)` accettano un elemento opzionale
 * per pattern declarativo React (montare un `<video ref>` JSX e bind via
 * effect). Comportamento: se passato l'element, il track lo riusa; se omesso,
 * livekit ne crea uno nuovo.
 */
export interface RemoteTrack {
  readonly kind: 'audio' | 'video' | 'data';
  readonly name: string;
  readonly sid: string;
  attach(element?: HTMLMediaElement): HTMLMediaElement;
  detach(element?: HTMLMediaElement): HTMLMediaElement[];
  on(event: 'message', cb: (data: string) => void): unknown;
  on(event: string, cb: (...args: unknown[]) => void): unknown;
}

export interface RemoteTrackPublication {
  readonly trackSid: string;
  readonly trackName: string;
  readonly kind: 'audio' | 'video' | 'data';
  readonly isSubscribed: boolean;
  readonly track?: RemoteTrack;
  readonly _lk?: LKRemoteTrackPublication;
}
export type LocalTrackPublication = LKLocalTrackPublication;

interface ConnectOptions {
  name?: string;
  tracks?: Array<LocalVideoTrackInstance | LocalAudioTrackInstance | LocalDataTrack>;
  wsUrl?: string;
  dominantSpeaker?: boolean;
  networkQuality?: unknown;
  preferredVideoCodecs?: unknown;
  bandwidthProfile?: unknown;
}

const networkQualityFromConnectionQuality = (q: ConnectionQuality): number => {
  switch (q) {
    case ConnectionQuality.Excellent:
      return 5;
    case ConnectionQuality.Good:
      return 4;
    case ConnectionQuality.Poor:
      return 2;
    case ConnectionQuality.Lost:
      return 0;
    case ConnectionQuality.Unknown:
    default:
      return 3;
  }
};

const trackName = (pub: LKRemoteTrackPublication | LKLocalTrackPublication): string => {
  if (pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio) {
    return 'screen';
  }
  return pub.trackName || pub.trackSid || '';
};

const decorateRemoteTrack = (track: LKRemoteTrack, pub: LKRemoteTrackPublication): RemoteTrack => {
  const t = track as LKRemoteTrack & { name?: string; sid?: string };
  if (!t.name) t.name = trackName(pub);
  if (!t.sid) t.sid = pub.trackSid;
  return t as unknown as RemoteTrack;
};

const dataTrackAsRemote = (dt: RemoteDataTrack): RemoteTrack => dt as unknown as RemoteTrack;

const dataPubAsRemote = (dt: RemoteDataTrack): RemoteTrackPublication => ({
  trackSid: dt.sid,
  trackName: dt.name,
  kind: 'data',
  isSubscribed: true,
  track: dataTrackAsRemote(dt),
});

const adaptRemotePub = (pub: LKRemoteTrackPublication): RemoteTrackPublication => ({
  trackSid: pub.trackSid,
  trackName: trackName(pub),
  kind: (pub.kind === Track.Kind.Audio ? 'audio' : 'video') as 'audio' | 'video',
  isSubscribed: pub.isSubscribed,
  track: pub.track ? (decorateRemoteTrack(pub.track, pub) as RemoteTrack) : undefined,
  _lk: pub,
});

/* ─── Synthetic RemoteDataTrack: convoglia `RoomEvent.DataReceived` ────── */

export class RemoteDataTrack extends EventEmitter {
  readonly kind = 'data' as const;
  readonly name = 'data';
  readonly sid: string;

  constructor(participantIdentity: string) {
    super();
    this.sid = `data-${participantIdentity}`;
  }
}

/* ─── RemoteParticipant adapter ─────────────────────────────────────────── */

class RemoteParticipantAdapter extends EventEmitter {
  identity: string;
  sid: string;
  /** Map<trackSid, RemoteTrackPublication> — collezione publication remote per participant. */
  tracks: Map<string, RemoteTrackPublication>;
  /** Data track sintetico esposto come RemoteDataTrack subscribed (alimentato da DataPacket LiveKit). */
  _dataTrack: RemoteDataTrack;
  /** Riferimento all'oggetto livekit nativo. */
  _lk: LKRemoteParticipant;

  constructor(p: LKRemoteParticipant) {
    super();
    this._lk = p;
    this.identity = p.identity;
    this.sid = p.sid;
    this.tracks = new Map();
    this._dataTrack = new RemoteDataTrack(p.identity);

    for (const [sid, pub] of p.trackPublications.entries()) {
      this.tracks.set(sid, adaptRemotePub(pub as LKRemoteTrackPublication));
    }
    // Pubblicazione sintetica per il data track esposta come RemoteDataTrack
    // subscribed. VideoCall.tsx itera con
    // `participant.tracks.forEach(pub => handleTrackSubscribed(pub.track, ...))`
    // per recuperarla.
    this.tracks.set(this._dataTrack.sid, dataPubAsRemote(this._dataTrack));

    p.on(ParticipantEvent.TrackPublished, (pub: LKRemoteTrackPublication) => {
      this.tracks.set(pub.trackSid, adaptRemotePub(pub));
    });
    p.on(ParticipantEvent.TrackUnpublished, (pub: LKRemoteTrackPublication) => {
      this.tracks.delete(pub.trackSid);
    });
    p.on(ParticipantEvent.TrackSubscribed, (track: LKRemoteTrack, pub: LKRemoteTrackPublication) => {
      this.tracks.set(pub.trackSid, adaptRemotePub(pub));
      this.emit('trackSubscribed', decorateRemoteTrack(track, pub), pub, this);
    });
    p.on(ParticipantEvent.TrackUnsubscribed, (track: LKRemoteTrack, pub: LKRemoteTrackPublication) => {
      this.emit('trackUnsubscribed', decorateRemoteTrack(track, pub), pub, this);
    });
    // Re-emit eventi mute/unmute LiveKit nei nomi attesi da VideoCall.tsx:
    //   LiveKit TrackMuted   → "trackDisabled"
    //   LiveKit TrackUnmuted → "trackEnabled"
    p.on(ParticipantEvent.TrackMuted, (pub) => {
      this.emit('trackDisabled', adaptRemotePub(pub as LKRemoteTrackPublication), this);
    });
    p.on(ParticipantEvent.TrackUnmuted, (pub) => {
      this.emit('trackEnabled', adaptRemotePub(pub as LKRemoteTrackPublication), this);
    });
    p.on(ParticipantEvent.ConnectionQualityChanged, (q: ConnectionQuality) => {
      this.emit('networkQualityLevelChanged', networkQualityFromConnectionQuality(q));
    });
  }

  /** @internal Chiamato dal Room quando arriva data da questo participant. */
  _emitData(payload: Uint8Array): void {
    try {
      const text = new TextDecoder().decode(payload);
      this._dataTrack.emit('message', text);
    } catch {
      // ignore
    }
  }
}

export type RemoteParticipant = RemoteParticipantAdapter;

/* ─── LocalDataTrack: bufferizza fino al bind, poi `publishData(reliable)` ─ */

export class LocalDataTrack {
  readonly kind = 'data' as const;
  name: string;
  private lp: LKLocalParticipant | null = null;
  private buffer: string[] = [];

  constructor(opts?: { name?: string }) {
    this.name = opts?.name || 'data';
  }

  /** @internal */
  _bind(lp: LKLocalParticipant): void {
    this.lp = lp;
    for (const msg of this.buffer) this.send(msg);
    this.buffer = [];
  }

  send(payload: string | ArrayBuffer): void {
    if (!this.lp) {
      if (typeof payload === 'string') this.buffer.push(payload);
      return;
    }
    const data = typeof payload === 'string' ? new TextEncoder().encode(payload) : new Uint8Array(payload);
    void this.lp.publishData(data, { reliable: true });
  }
}

/* ─── LocalParticipant adapter ──────────────────────────────────────────── */

interface DataTrackPublicationLike {
  track: LocalDataTrack;
  trackName: string;
}

class LocalParticipantAdapter {
  private lp: LKLocalParticipant;
  identity: string;
  /** Map sid→publication dei LocalDataTrack pubblicati. */
  dataTracks: Map<string, DataTrackPublicationLike>;

  constructor(lp: LKLocalParticipant) {
    this.lp = lp;
    this.identity = lp.identity;
    this.dataTracks = new Map();
  }

  publishTrack(track: LocalVideoTrackInstance | LocalAudioTrackInstance | LocalDataTrack): void {
    if (track instanceof LocalDataTrack) {
      track._bind(this.lp);
      const sid = `data-${this.identity}-${this.dataTracks.size}`;
      this.dataTracks.set(sid, { track, trackName: track.name });
      return;
    }
    void this.lp.publishTrack(track, { name: (track as LKLocalVideoTrack | LKLocalAudioTrack).mediaStreamTrack?.label });
  }

  unpublishTrack(track: LocalVideoTrackInstance | LocalAudioTrackInstance | LocalDataTrack): void {
    if (track instanceof LocalDataTrack) {
      for (const [sid, pub] of this.dataTracks.entries()) {
        if (pub.track === track) this.dataTracks.delete(sid);
      }
      return;
    }
    // Swallow rejection: durante teardown la peer connection può essere già
    // chiusa (`could not createOffer with closed peer connection`). Non è
    // un errore actionable per l'utente.
    void this.lp.unpublishTrack(track).catch(() => undefined);
  }
}

export type LocalParticipant = LocalParticipantAdapter;

/* ─── Room adapter ──────────────────────────────────────────────────────── */

class RoomAdapter extends EventEmitter {
  private lkRoom: LKRoom;
  participants: Map<string, RemoteParticipantAdapter>;
  localParticipant: LocalParticipantAdapter;
  name: string;

  constructor(lkRoom: LKRoom) {
    super();
    this.lkRoom = lkRoom;
    this.name = lkRoom.name;
    this.participants = new Map();
    this.localParticipant = new LocalParticipantAdapter(lkRoom.localParticipant);

    for (const [identity, p] of lkRoom.remoteParticipants.entries()) {
      this.participants.set(identity, new RemoteParticipantAdapter(p));
    }

    lkRoom.on(RoomEvent.ParticipantConnected, (p: LKRemoteParticipant) => {
      const adapter = new RemoteParticipantAdapter(p);
      this.participants.set(p.identity, adapter);
      this.emit('participantConnected', adapter);
    });
    lkRoom.on(RoomEvent.ParticipantDisconnected, (p: LKRemoteParticipant) => {
      const adapter = this.participants.get(p.identity);
      this.participants.delete(p.identity);
      if (adapter) this.emit('participantDisconnected', adapter);
    });
    lkRoom.on(RoomEvent.ActiveSpeakersChanged, (speakers: LKParticipant[]) => {
      const dominant = speakers.find((s) => s.identity !== lkRoom.localParticipant.identity);
      if (!dominant) return;
      const adapter = this.participants.get(dominant.identity);
      if (adapter) this.emit('dominantSpeakerChanged', adapter);
    });
    lkRoom.on(RoomEvent.Disconnected, () => {
      this.emit('disconnected', this);
    });
    lkRoom.on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: LKRemoteParticipant) => {
      if (!participant) return;
      const adapter = this.participants.get(participant.identity);
      if (adapter) adapter._emitData(payload);
    });
  }

  disconnect(): void {
    void this.lkRoom.disconnect();
  }

  /**
   * Stato corrente della peer connection LiveKit. Utilizzato dai consumer
   * per evitare unpublish/republish su connessione già chiusa
   * (`createOffer with closed peer connection`).
   */
  get state(): 'disconnected' | 'connecting' | 'connected' | 'reconnecting' {
    const s = (this.lkRoom as unknown as { state?: string }).state;
    if (s === 'connected' || s === 'connecting' || s === 'reconnecting') return s;
    return 'disconnected';
  }
}

export type Room = RoomAdapter;

/* ─── Video.connect ─────────────────────────────────────────────────────── */

/**
 * HA gap G3 — fallback URL cascade. Il DNS round-robin Cloudflare grigio sul
 * primario `media.easyflowai.it` non rimuove un IP nodo morto in tempo reale
 * (no health-check). Per evitare connect failure quando il client sceglie l'IP
 * morto, l'adapter tenta in cascata: primario → fallback-1 → fallback-2.
 *
 * I fallback puntano agli A-record vanity per-nodo (`media-1.easyflowai.it`
 * = BE-3, `media-2.easyflowai.it` = BE-4). Il signaling LiveKit gestisce
 * internamente il routing al nodo che ospita la room (state in Redis DB5
 * condiviso): il fallback URL serve SOLO a superare un IP morto a livello TCP.
 *
 * Env: `VITE_LIVEKIT_URL` (primario, obbligatorio) + opzionale
 * `VITE_LIVEKIT_URL_FALLBACKS` (CSV di wss URL). Timeout connect per
 * tentativo: 5s (rapido, copre TCP unreachable / TLS handshake bloccato).
 */
const CONNECT_TIMEOUT_MS = 5000;

interface ViteEnv {
  VITE_LIVEKIT_URL?: string;
  VITE_LIVEKIT_URL_FALLBACKS?: string;
}

function readViteEnv(): ViteEnv {
  return (import.meta as unknown as { env?: ViteEnv }).env ?? {};
}

function buildUrlChain(primary: string): string[] {
  const env = readViteEnv();
  const raw = env.VITE_LIVEKIT_URL_FALLBACKS ?? '';
  const fallbacks = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s !== primary);
  return [primary, ...fallbacks];
}

async function connectWithTimeout(
  lkRoom: LKRoom,
  url: string,
  token: string,
  timeoutMs: number,
): Promise<void> {
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(`connect timeout ${timeoutMs}ms`)), timeoutMs);
  });
  await Promise.race([lkRoom.connect(url, token, { autoSubscribe: true }), timeout]);
}

async function connect(token: string, opts: ConnectOptions = {}): Promise<RoomAdapter> {
  const env = readViteEnv();
  const primary = opts.wsUrl || env.VITE_LIVEKIT_URL;
  if (!primary) {
    throw new Error('LiveKit WS URL mancante: passa `opts.wsUrl` o setta `VITE_LIVEKIT_URL`');
  }

  const chain = buildUrlChain(primary);
  const errors: Array<{ url: string; message: string }> = [];

  for (let i = 0; i < chain.length; i += 1) {
    const url = chain[i] as string;
    const lkRoom = new LKRoom({
      adaptiveStream: true,
      dynacast: true,
      publishDefaults: { simulcast: true, videoCodec: 'vp8' },
    });

    try {
      await connectWithTimeout(lkRoom, url, token, CONNECT_TIMEOUT_MS);
      if (i > 0) {
        // eslint-disable-next-line no-console -- log diagnostico HA: visibile in DevTools per debugging fallback senza dipendere dal logger app.
        console.warn(`[LiveKit] primary failed, connected via fallback #${i} (${url})`);
      }
      const room = new RoomAdapter(lkRoom);
      if (opts.tracks?.length) {
        for (const t of opts.tracks) {
          room.localParticipant.publishTrack(t);
        }
      }
      return room;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      errors.push({ url, message });
      try {
        await lkRoom.disconnect();
      } catch {
        /* ignore: connect failed, peer-connection già chiusa */
      }
      // eslint-disable-next-line no-console -- log diagnostico HA: visibile in DevTools per debugging fallback senza dipendere dal logger app.
      console.warn(`[LiveKit] connect failed ${url}: ${message}${i < chain.length - 1 ? ' — trying next' : ''}`);
    }
  }

  const summary = errors.map((e) => `${e.url}: ${e.message}`).join(' | ');
  throw new Error(`LiveKit connect failed on all ${chain.length} endpoint(s): ${summary}`);
}

export const Video = { connect };
export default Video;

/* ─── createLocalVideoTrack / createLocalAudioTrack ─────────────────────── */

interface CreateLocalVideoTrackOptions {
  width?: number;
  height?: number;
  facingMode?: 'user' | 'environment';
  name?: string;
}

export async function createLocalVideoTrack(opts: CreateLocalVideoTrackOptions = {}): Promise<LocalVideoTrackInstance> {
  const t = await lkCreateLocalVideoTrack({
    resolution: opts.width && opts.height ? { width: opts.width, height: opts.height, frameRate: 30 } : undefined,
    facingMode: opts.facingMode,
  });
  return t as LocalVideoTrackInstance;
}

export async function createLocalAudioTrack(): Promise<LocalAudioTrackInstance> {
  const t = await lkCreateLocalAudioTrack();
  return t as LocalAudioTrackInstance;
}
